/**
 * Hotkey scopes — the single source of truth for react-hotkeys-hook scoping.
 *
 * `global`  holds section action shortcuts (vulns/asset/jobs/technology
 *           Alt+letter, etc). Disabled while any overlay is open so a keystroke
 *           never mutates data behind a modal/drawer.
 * `overlay` holds Escape-to-dismiss handlers and element-gated shortcuts
 *           (e.g. attack-path copy/paste). Never globally disabled — each
 *           consumer self-gates via `enabled` / element ref.
 * `tableNav` holds table row arrow-nav; element-focus-gated, never globally
 *           suppressed (a focused table inside a drawer must still navigate).
 *
 * Keep this set deliberately minimal. Adding a scope is a real design decision,
 * not a convenience — prefer `enabled`/ref gating within an existing scope.
 */
export const HOTKEY_SCOPES = {
  global: 'global',
  overlay: 'overlay',
  tableNav: 'tableNav',
} as const;

export type HotkeyScope = (typeof HOTKEY_SCOPES)[keyof typeof HOTKEY_SCOPES];

/** Scopes active on a fresh mount, before any overlay opens. */
export const INITIAL_ACTIVE_SCOPES: HotkeyScope[] = [
  HOTKEY_SCOPES.global,
  HOTKEY_SCOPES.overlay,
  HOTKEY_SCOPES.tableNav,
];
